import { api, unwrap } from "./api";

export type WorkspaceInfo = { workspace: string; space: string | null };
export type Space = { id: string; name: string | null };

export type AppSettings = {
  id: number;
  clickupSpaceId: string | null;
  clickupSpaceName: string | null;
  completeStatus: string;
  reopenStatus: string;
  signoffStaleDays: number;
  kickbackStaleDays: number;
  autoSyncEnabled: boolean;
  archiveCutoffDate: string | null;
};

/** What applying a given cutoff date would do (counts only — no mutation). */
export type ArchivePreview = {
  cutoff: string;
  toArchive: number;
  toRestore: number;
  /** all cutoff-archived tasks in this space — the per-space restore-button signal */
  cutoffArchived: number;
  remainingActive: number;
  archivedAfter: number;
};

/** Active workspace (team) + watched space, for the header. */
export async function fetchWorkspace(): Promise<WorkspaceInfo> {
  return unwrap(api.api.workspace.get());
}

/** Spaces that currently hold tasks — powers the per-viewer space switcher. */
export async function fetchSpaces(): Promise<Space[]> {
  const { spaces } = await unwrap(api.api.spaces.get());
  return spaces;
}

export async function fetchSettings(): Promise<AppSettings> {
  return unwrap(api.api.settings.get());
}

/** The admin-editable settings fields (server-validated by the PUT body schema). */
export type SettingsUpdate = Omit<
  Partial<AppSettings>,
  "id" | "clickupSpaceName"
>;

export async function saveSettings(
  patch: SettingsUpdate,
): Promise<AppSettings> {
  return unwrap(api.api.settings.put(patch));
}

/** Preview a go-live cutoff (YYYY-MM-DD) for a space — how many it archives/restores. */
export async function fetchArchivePreview(
  cutoff: string,
  spaceId: string,
): Promise<ArchivePreview> {
  return unwrap(
    api.api.archive.preview.get({ query: { cutoff, space_id: spaceId } }),
  );
}

/** Apply the cutoff to a space: reversible reconcile + persist the date. */
export async function applyArchiveCutoff(
  cutoff: string,
  spaceId: string,
): Promise<{ archived: number; restored: number }> {
  return unwrap(api.api.archive.apply.post({ cutoff, space_id: spaceId }));
}

/** Bring back every cutoff-archived task in a space. */
export async function restoreAllArchived(
  spaceId: string,
): Promise<{ restored: number }> {
  return unwrap(api.api.archive["restore-all"].post({ space_id: spaceId }));
}

/**
 * Turn a stored archive-cutoff value into a Date for the picker, or null when
 * there isn't a usable one (so the picker keeps its "today" default).
 *
 * The API sends a date-only `YYYY-MM-DD` string, but we parse defensively: a
 * fuller shape (e.g. a Date that reached us as a full ISO string — say from a
 * persisted cache or an older build) is reduced to its calendar date, and an
 * unparseable value yields null rather than an Invalid Date. This matters
 * because the result feeds `format()`, which throws "Invalid time value" on an
 * invalid Date and would otherwise crash the whole Settings route.
 */
export function parseCutoffDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  // Keep the YYYY-MM-DD head of either "2026-06-11" or "2026-06-11T00:00:00Z",
  // then parse as local midnight.
  const parsed = new Date(`${String(raw).slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
